import { createFileRoute, Outlet, Link, useLocation, redirect } from '@tanstack/react-router';
import {
  Users,
  Building2,
  Activity,
  FileText,
  Database,
  Shield,
  Key,
  ChevronRight,
  Lock,
  UserCog,
  Code,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAdmin } from '@/contexts/AdminContext';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

export const Route = createFileRoute('/_dashboard/admin')({
  component: AdminLayout,
});

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
  superAdminOnly?: boolean;
}

const superAdminNav: NavItem[] = [
  { name: 'Dashboard', href: '/admin/superadmin', icon: Activity },
  { name: 'Users', href: '/admin/superadmin/users', icon: Users },
  { name: 'Organizations', href: '/admin/superadmin/organizations', icon: Building2 },
  { name: 'System Health', href: '/admin/superadmin/system', icon: Activity },
];

const orgAdminNav: NavItem[] = [
  { name: 'Dashboard', href: '/admin/org', icon: Activity },
  { name: 'Security', href: '/admin/org/security', icon: Shield, badge: 'New' },
  { name: 'Team Members', href: '/admin/org/users', icon: Users },
  { name: 'Roles', href: '/admin/org/roles', icon: Shield },
  { name: 'Policies', href: '/admin/org/policies', icon: FileText },
  { name: 'API Keys', href: '/admin/org/api-keys', icon: Key },
  { name: 'SSO', href: '/admin/org/sso', icon: Lock },
  { name: 'SCIM', href: '/admin/org/scim', icon: UserCog },
  { name: 'SDK Keys', href: '/admin/org/sdk-keys', icon: Code },
];

const sharedNav: NavItem[] = [
  { name: 'Audit Logs', href: '/admin/audit', icon: FileText },
];

const dataViewerNav: NavItem[] = [
  { name: 'PostgreSQL', href: '/admin/data/postgres', icon: Database, superAdminOnly: true },
  { name: 'Redis', href: '/admin/data/redis', icon: Database, superAdminOnly: true },
  { name: 'ClickHouse', href: '/admin/data/clickhouse', icon: Database, superAdminOnly: true },
];

function AdminLayout() {
  const location = useLocation();
  const pathname = location.pathname;
  const { isAdmin, isSuperAdmin, isOrgAdmin, isLoading } = useAdmin();

  if (isLoading) {
    return (
      <div className="flex gap-6">
        <div className="w-64 shrink-0 space-y-4">
          <Skeleton className="h-8 w-32" />
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        </div>
        <div className="flex-1">
          <Skeleton className="h-[400px] w-full" />
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  // Get breadcrumb from pathname
  const getBreadcrumb = () => {
    const parts = pathname?.split('/').filter(Boolean) || [];
    return parts.map((part, index) => ({
      name: part.charAt(0).toUpperCase() + part.slice(1),
      href: '/' + parts.slice(0, index + 1).join('/'),
      isLast: index === parts.length - 1,
    }));
  };

  const breadcrumb = getBreadcrumb();

  return (
    <div className="flex gap-6">
      {/* Admin Sidebar */}
      <div className="w-64 shrink-0 space-y-6">
        {/* Superadmin Section */}
        {isSuperAdmin && (
          <div className="space-y-1">
            <h3 className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Platform Admin
            </h3>
            {superAdminNav.map((item) => {
              const isActive = pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.name}</span>
                  {item.badge && (
                    <Badge variant="secondary" className="ml-auto">
                      {item.badge}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </div>
        )}

        {/* Org Admin Section */}
        {isOrgAdmin && (
          <div className="space-y-1">
            <h3 className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Organization Admin
            </h3>
            {orgAdminNav.map((item) => {
              const isActive = pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </div>
        )}

        {/* Shared Section */}
        <div className="space-y-1">
          <h3 className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Monitoring
          </h3>
          {sharedNav.map((item) => {
            const isActive = pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <item.icon className="h-4 w-4" />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </div>

        {/* Data Viewers (Superadmin only) */}
        {isSuperAdmin && (
          <div className="space-y-1">
            <h3 className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Data Viewers
            </h3>
            {dataViewerNav.map((item) => {
              const isActive = pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1 text-sm text-muted-foreground mb-6">
          {breadcrumb.map((item, index) => (
            <span key={item.href} className="flex items-center gap-1">
              {index > 0 && <ChevronRight className="h-4 w-4" />}
              {item.isLast ? (
                <span className="font-medium text-foreground">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-foreground">
                  {item.name}
                </Link>
              )}
            </span>
          ))}
        </nav>

        {/* Page Content */}
        <Outlet />
      </div>
    </div>
  );
}
